import { copyText } from '../utils/clipboard';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { get, post } from '../utils/api';

const outcomes = [
  ['delivered', 'Delivered successfully'],
  ['customer_unavailable', 'Customer unavailable'],
  ['customer_unreachable', 'Customer unreachable'],
  ['customer_rescheduled', 'Customer rescheduled'],
  ['wrong_address', 'Wrong or incomplete address'],
  ['rider_failure', 'Rider failure'],
  ['provider_failure', 'Logistics provider failure'],
  ['bloans_failure', 'Bloans operational failure'],
  ['returned', 'Package returned']
];

const proofTypes = [
  ['customer_confirmation_note', 'Written customer confirmation'],
  ['signed_delivery_note', 'Signed delivery note'],
  ['delivery_photo', 'Delivery photograph'],
  ['rider_confirmation', 'Rider confirmation'],
  ['logistics_confirmation', 'Logistics company confirmation']
];

const postDispatchStatuses = [
  'dispatched',
  'out_for_delivery',
  'delivery_attempt_failed',
  'awaiting_proof',
  'awaiting_completion',
  'ready_for_pickup',
  'delivered',
  'picked_up'
];

const failedOutcomes = [
  'customer_unavailable',
  'customer_unreachable',
  'customer_rescheduled',
  'wrong_address',
  'rider_failure',
  'provider_failure',
  'bloans_failure',
  'returned'
];

const customerCausedOutcomes = [
  'customer_unavailable',
  'customer_unreachable',
  'customer_rescheduled',
  'wrong_address'
];

const panel = {
  marginTop: 14,
  padding: 16,
  background: '#FFFEFB',
  border: '1px solid #DDD5C5',
  borderRadius: 12
};

const grid = {
  display: 'grid',
  gridTemplateColumns:
    'repeat(auto-fit,minmax(220px,1fr))',
  gap: 12
};

const label = {
  display: 'block',
  marginBottom: 5,
  color: '#3B2A21',
  fontSize: 12,
  fontWeight: 700
};

const field = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 11px',
  border: '1px solid #CFC6B7',
  borderRadius: 8,
  background: '#FFFFFF',
  fontSize: 13
};

const button = {
  padding: '10px 15px',
  border: 'none',
  borderRadius: 8,
  background: '#556B2F',
  color: '#FFFFFF',
  fontWeight: 700,
  cursor: 'pointer'
};

const initialAttempt = {
  outcome: 'delivered',
  failure_reason: '',
  notes: '',
  customer_contacted: true,
  customer_caused: false,
  additional_fee: '',
  additional_fee_paid_to_rider: false,
  attempted_at: ''
};

const initialProof = {
  proof_type: 'customer_confirmation_note',
  confirmation_note: '',
  customer_confirmation_obtained: false,
  file: null
};

const token = () =>
  localStorage.getItem('bb_token') ||
  localStorage.getItem('token') ||
  '';

const extractRows = response => {
  const value =
    response?.data ||
    response?.history ||
    response?.messages ||
    response ||
    [];

  return Array.isArray(value) ? value : [];
};

const extractPreview = response =>
  response?.data || response || null;

const formatMoney = value =>
  Number(value || 0).toLocaleString(
    'en-NG',
    {
      style: 'currency',
      currency: 'NGN'
    }
  );

export default function ControlledDeliveryCompletion({
  delivery,
  onRefresh
}) {
  const [attempt, setAttempt] =
    useState(initialAttempt);
  const [proof, setProof] =
    useState(initialProof);
  const [completionNote, setCompletionNote] =
    useState('');
  const [preview, setPreview] =
    useState(null);
  const [history, setHistory] =
    useState([]);
  const [busy, setBusy] =
    useState('');
  const [error, setError] =
    useState('');
  const [notice, setNotice] =
    useState('');

  const id = delivery?.id;
  const status = delivery?.status || '';
  const attemptCount =
    Number(delivery?.attempt_count || 0);
  const proofStatus =
    delivery?.proof_status || 'pending';

  const isPostDispatch =
    postDispatchStatuses.includes(status);

  const canRecordAttempt =
    isPostDispatch &&
    ![
      'awaiting_completion',
      'delivered',
      'picked_up'
    ].includes(status) &&
    attemptCount < 3;

  const canSubmitProof =
    [
      'dispatched',
      'out_for_delivery',
      'delivery_attempt_failed',
      'awaiting_proof',
      'ready_for_pickup'
    ].includes(status);

  const canComplete =
    status === 'awaiting_completion';

  const loadTracking = useCallback(
    async () => {
      if (!id || !isPostDispatch) {
        return;
      }

      try {
        const previewResponse =
          await get(
            '/deliveries/' +
            id +
            '/tracking-preview'
          );

        setPreview(
          extractPreview(previewResponse)
        );
      } catch (loadError) {
        setPreview(null);
      }

      try {
        const historyResponse =
          await get(
            '/deliveries/' +
            id +
            '/tracking-history'
          );

        setHistory(
          extractRows(historyResponse)
        );
      } catch (loadError) {
        setHistory([]);
      }
    },
    [id, isPostDispatch]
  );

  useEffect(() => {
    loadTracking();
  }, [loadTracking]);

  const finishAction = async message => {
    setNotice(message);

    await loadTracking();

    if (typeof onRefresh === 'function') {
      await onRefresh();
    }
  };

  const run = async (
    actionName,
    action,
    successMessage
  ) => {
    setBusy(actionName);
    setError('');
    setNotice('');

    try {
      const response = await action();

      await finishAction(
        response?.message ||
        successMessage
      );

      return response;
    } catch (actionError) {
      setError(
        actionError?.message ||
        actionError?.error ||
        'The delivery action failed.'
      );

      return null;
    } finally {
      setBusy('');
    }
  };

  const submitAttempt = async event => {
    event.preventDefault();

    const failed =
      failedOutcomes.includes(
        attempt.outcome
      );

    if (
      failed &&
      !attempt.customer_contacted
    ) {
      setError(
        'Contact the customer before recording an unsuccessful delivery attempt.'
      );
      return;
    }

    if (
      failed &&
      !attempt.failure_reason.trim() &&
      !attempt.notes.trim()
    ) {
      setError(
        'Enter the reason the delivery attempt was unsuccessful.'
      );
      return;
    }

    if (
      attempt.customer_caused &&
      Number(attempt.additional_fee || 0) <= 0
    ) {
      setError(
        'Enter the additional delivery fee payable to the rider.'
      );
      return;
    }

    const response = await run(
      'attempt',
      () =>
        post(
          '/deliveries/' +
          id +
          '/attempts',
          {
            ...attempt,
            additional_fee:
              Number(
                attempt.additional_fee || 0
              ),
            attempted_at:
              attempt.attempted_at || null
          }
        ),
      attempt.outcome === 'delivered'
        ? 'Successful delivery recorded. Proof is now required.'
        : 'Delivery attempt recorded.'
    );

    if (response) {
      setAttempt(initialAttempt);
    }
  };

  const submitProof = async event => {
    event.preventDefault();

    const written =
      proof.proof_type ===
      'customer_confirmation_note';

    if (
      written &&
      proof.confirmation_note.trim().length <
        15
    ) {
      setError(
        'The customer-confirmation note must contain at least 15 characters.'
      );
      return;
    }

    if (
      written &&
      !proof.customer_confirmation_obtained
    ) {
      setError(
        'Confirm that the customer acknowledged receiving the package.'
      );
      return;
    }

    if (!written && !proof.file) {
      setError(
        'Attach the selected proof of delivery.'
      );
      return;
    }

    const form = new FormData();

    form.append(
      'proof_type',
      proof.proof_type
    );
    form.append(
      'confirmation_note',
      proof.confirmation_note.trim()
    );
    form.append(
      'customer_confirmation_obtained',
      String(
        proof.customer_confirmation_obtained
      )
    );

    if (proof.file) {
      form.append('proof', proof.file);
    }

    const response = await run(
      'proof',
      async () => {
        const result = await fetch(
          '/api/deliveries/' +
          id +
          '/proofs',
          {
            method: 'POST',
            headers: token()
              ? {
                  Authorization:
                    'Bearer ' + token()
                }
              : {},
            body: form
          }
        );

        const text = await result.text();
        let data = {};

        if (text) {
          try {
            data = JSON.parse(text);
          } catch (parseError) {
            data = { message: text };
          }
        }

        if (!result.ok) {
          throw new Error(
            data.error ||
            data.message ||
            'Proof upload failed.'
          );
        }

        return data;
      },
      'Proof of delivery submitted.'
    );

    if (response) {
      setProof(initialProof);

      const input =
        document.getElementById(
          'delivery-proof-' + id
        );

      if (input) {
        input.value = '';
      }
    }
  };

  const completeDelivery = async () => {
    const response = await run(
      'complete',
      () =>
        post(
          '/deliveries/' +
          id +
          '/complete',
          {
            completion_note:
              completionNote.trim()
          }
        ),
      'Delivery completed successfully.'
    );

    if (response) {
      setCompletionNote('');
    }
  };

  const recipients = useMemo(() => {
    if (
      Array.isArray(preview?.recipients)
    ) {
      return preview.recipients;
    }

    if (
      preview?.recipient &&
      typeof preview.recipient === 'object'
    ) {
      return [preview.recipient];
    }

    return [];
  }, [preview]);

  const communicate = async (
    channel,
    recipient
  ) => {
    const recipientType =
      recipient.recipient_type ||
      recipient.type ||
      'buyer';

    const response = await run(
      channel + '-' + recipientType,
      () =>
        post(
          '/deliveries/' +
          id +
          '/tracking-message',
          {
            channel,
            recipient_type:
              recipientType
          }
        ),
      channel === 'email'
        ? 'Tracking email sent.'
        : channel === 'whatsapp'
          ? 'WhatsApp tracking message prepared.'
          : 'Tracking message recorded.'
    );

    if (!response) {
      return;
    }

    const message =
      recipient.message ||
      preview?.message ||
      response?.message_text ||
      response?.data?.message_text ||
      '';

    if (channel === 'copy') {
      try {
        await copyText(
          message
        );

        setNotice(
          'Tracking message copied to the clipboard.'
        );
      } catch (copyError) {
        setNotice(
          'Automatic copying failed. Select and copy the displayed tracking message manually.'
        );
      }
    }

    if (channel === 'whatsapp') {
      const suppliedUrl =
        response?.whatsapp_url ||
        response?.data?.whatsapp_url;

      const phone = String(
        recipient.phone || ''
      ).replace(/\D/g, '');

      const fallbackUrl =
        phone && message
          ? 'https://wa.me/' +
            phone +
            '?text=' +
            encodeURIComponent(message)
          : '';

      const whatsappUrl =
        suppliedUrl || fallbackUrl;

      if (whatsappUrl) {
        window.open(
          whatsappUrl,
          '_blank',
          'noopener,noreferrer'
        );
      }
    }
  };

  if (!isPostDispatch) {
    return null;
  }

  return (
    <div style={{marginTop:18}}>
      <div style={{
        ...panel,
        background:'#E8F5EB',
        borderColor:'#B8D8BE',
        color:'#2E6B3A',
        fontSize:12
      }}>
        <strong>
          Package released for delivery
        </strong>

        <div style={{marginTop:4}}>
          Record each attempt, submit proof,
          complete the delivery and send the
          approved tracking communication.
        </div>
      </div>

      {error && (
        <div style={{
          ...panel,
          background:'#FDEAEA',
          borderColor:'#E2B7B2',
          color:'#8F2E24',
          fontSize:12
        }}>
          {error}
        </div>
      )}

      {notice && (
        <div style={{
          ...panel,
          background:'#E8F5EB',
          borderColor:'#B8D8BE',
          color:'#2E6B3A',
          fontSize:12
        }}>
          {notice}
        </div>
      )}

      <div style={panel}>
        <div style={grid}>
          <div>
            <span style={label}>
              Delivery status
            </span>
            {status.replace(/_/g, ' ')}
          </div>

          <div>
            <span style={label}>
              Attempts
            </span>
            {attemptCount} of 3
          </div>

          <div>
            <span style={label}>
              Proof status
            </span>
            {proofStatus.replace(/_/g, ' ')}
          </div>
        </div>
      </div>

      {canRecordAttempt && (
        <form
          onSubmit={submitAttempt}
          style={panel}
        >
          <h4 style={{margin:'0 0 12px'}}>
            Record Delivery Attempt
          </h4>

          <div style={grid}>
            <div>
              <label style={label}>
                Attempt Outcome
              </label>

              <select
                style={field}
                value={attempt.outcome}
                onChange={event => {
                  const outcome =
                    event.target.value;

                  setAttempt(current => ({
                    ...current,
                    outcome,
                    customer_caused:
                      customerCausedOutcomes
                        .includes(outcome)
                        ? current
                            .customer_caused
                        : false,
                    additional_fee:
                      customerCausedOutcomes
                        .includes(outcome)
                        ? current
                            .additional_fee
                        : '',
                    additional_fee_paid_to_rider:
                      customerCausedOutcomes
                        .includes(outcome)
                        ? current
                            .additional_fee_paid_to_rider
                        : false
                  }));
                }}
              >
                {outcomes.map(
                  ([value, text]) => (
                    <option
                      key={value}
                      value={value}
                    >
                      {text}
                    </option>
                  )
                )}
              </select>
            </div>

            <div>
              <label style={label}>
                Attempt Date and Time
              </label>

              <input
                type="datetime-local"
                style={field}
                value={attempt.attempted_at}
                onChange={event =>
                  setAttempt(current => ({
                    ...current,
                    attempted_at:
                      event.target.value
                  }))
                }
              />
            </div>
          </div>

          {attempt.outcome !==
            'delivered' && (
            <>
              <div style={{marginTop:12}}>
                <label style={label}>
                  Failure Reason
                </label>

                <textarea
                  style={field}
                  rows={3}
                  value={
                    attempt.failure_reason
                  }
                  onChange={event =>
                    setAttempt(current => ({
                      ...current,
                      failure_reason:
                        event.target.value
                    }))
                  }
                  placeholder="Explain what prevented delivery."
                />
              </div>

              <div style={{
                display:'flex',
                flexDirection:'column',
                gap:8,
                marginTop:12,
                fontSize:12
              }}>
                <label>
                  <input
                    type="checkbox"
                    checked={
                      attempt.customer_contacted
                    }
                    onChange={event =>
                      setAttempt(current => ({
                        ...current,
                        customer_contacted:
                          event.target.checked
                      }))
                    }
                  />{' '}
                  Customer was contacted about
                  availability
                </label>

                {customerCausedOutcomes
                  .includes(
                    attempt.outcome
                  ) && (
                  <label>
                    <input
                      type="checkbox"
                      checked={
                        attempt.customer_caused
                      }
                      onChange={event =>
                        setAttempt(current => ({
                          ...current,
                          customer_caused:
                            event.target.checked
                        }))
                      }
                    />{' '}
                    Customer caused this
                    additional attempt
                  </label>
                )}
              </div>

              {attempt.customer_caused && (
                <div style={{
                  ...grid,
                  marginTop:12
                }}>
                  <div>
                    <label style={label}>
                      Additional Rider Fee
                    </label>

                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      style={field}
                      value={
                        attempt.additional_fee
                      }
                      onChange={event =>
                        setAttempt(current => ({
                          ...current,
                          additional_fee:
                            event.target.value
                        }))
                      }
                    />

                    {attempt.additional_fee && (
                      <div style={{
                        marginTop:4,
                        color:'#777',
                        fontSize:11
                      }}>
                        Customer pays rider:
                        {' '}
                        {formatMoney(
                          attempt.additional_fee
                        )}
                      </div>
                    )}
                  </div>

                  <label style={{
                    alignSelf:'end',
                    paddingBottom:10,
                    fontSize:12
                  }}>
                    <input
                      type="checkbox"
                      checked={
                        attempt
                          .additional_fee_paid_to_rider
                      }
                      onChange={event =>
                        setAttempt(current => ({
                          ...current,
                          additional_fee_paid_to_rider:
                            event.target.checked
                        }))
                      }
                    />{' '}
                    Customer paid the rider
                    directly
                  </label>
                </div>
              )}
            </>
          )}

          <div style={{marginTop:12}}>
            <label style={label}>
              Attempt Notes
            </label>

            <textarea
              style={field}
              rows={3}
              value={attempt.notes}
              onChange={event =>
                setAttempt(current => ({
                  ...current,
                  notes:event.target.value
                }))
              }
              placeholder="Add rider feedback or customer response."
            />
          </div>

          <button
            type="submit"
            style={{
              ...button,
              marginTop:12
            }}
            disabled={busy === 'attempt'}
          >
            {busy === 'attempt'
              ? 'Recording...'
              : 'Record Delivery Attempt'}
          </button>
        </form>
      )}

      {attemptCount >= 3 &&
        ![
          'awaiting_proof',
          'awaiting_completion',
          'delivered',
          'picked_up'
        ].includes(status) && (
          <div style={{
            ...panel,
            background:'#FDEAEA',
            borderColor:'#E2B7B2',
            color:'#8F2E24',
            fontSize:12
          }}>
            The maximum of three delivery
            attempts has been reached. Escalate
            the delivery before taking another
            action.
          </div>
        )}

      {canSubmitProof && (
        <form
          onSubmit={submitProof}
          style={panel}
        >
          <h4 style={{margin:'0 0 6px'}}>
            Submit Proof of Delivery
          </h4>

          <p style={{
            margin:'0 0 12px',
            color:'#777',
            fontSize:12
          }}>
            Every delivery requires proof. A
            written customer-confirmation note
            may be used for a small delivery.
          </p>

          <div style={grid}>
            <div>
              <label style={label}>
                Proof Type
              </label>

              <select
                style={field}
                value={proof.proof_type}
                onChange={event =>
                  setProof({
                    ...initialProof,
                    proof_type:
                      event.target.value
                  })
                }
              >
                {proofTypes.map(
                  ([value, text]) => (
                    <option
                      key={value}
                      value={value}
                    >
                      {text}
                    </option>
                  )
                )}
              </select>
            </div>

            {proof.proof_type !==
              'customer_confirmation_note' && (
              <div>
                <label style={label}>
                  Proof Attachment
                </label>

                <input
                  id={
                    'delivery-proof-' + id
                  }
                  type="file"
                  accept="image/*,.pdf"
                  style={field}
                  onChange={event =>
                    setProof(current => ({
                      ...current,
                      file:
                        event.target
                          .files?.[0] ||
                        null
                    }))
                  }
                />
              </div>
            )}
          </div>

          <div style={{marginTop:12}}>
            <label style={label}>
              Confirmation Note
            </label>

            <textarea
              style={field}
              rows={3}
              value={
                proof.confirmation_note
              }
              onChange={event =>
                setProof(current => ({
                  ...current,
                  confirmation_note:
                    event.target.value
                }))
              }
              placeholder="Customer confirmed that the complete package was received in good condition."
            />
          </div>

          <label style={{
            display:'block',
            marginTop:10,
            fontSize:12
          }}>
            <input
              type="checkbox"
              checked={
                proof.customer_confirmation_obtained
              }
              onChange={event =>
                setProof(current => ({
                  ...current,
                  customer_confirmation_obtained:
                    event.target.checked
                }))
              }
            />{' '}
            Customer confirmation was obtained
          </label>

          <button
            type="submit"
            style={{
              ...button,
              marginTop:12,
              background:'#A8893D'
            }}
            disabled={busy === 'proof'}
          >
            {busy === 'proof'
              ? 'Submitting...'
              : 'Submit Proof of Delivery'}
          </button>
        </form>
      )}

      {canComplete && (
        <div style={panel}>
          <h4 style={{margin:'0 0 6px'}}>
            Complete Delivery
          </h4>

          <p style={{
            color:'#777',
            fontSize:12
          }}>
            Complete the delivery only after
            valid proof has been submitted.
          </p>

          <textarea
            style={field}
            rows={3}
            value={completionNote}
            onChange={event =>
              setCompletionNote(
                event.target.value
              )
            }
            placeholder="Optional final completion note."
          />

          <button
            type="button"
            style={{
              ...button,
              marginTop:12
            }}
            onClick={completeDelivery}
            disabled={busy === 'complete'}
          >
            {busy === 'complete'
              ? 'Completing...'
              : 'Confirm Delivery Completed'}
          </button>
        </div>
      )}

      <div style={panel}>
        <h4 style={{margin:'0 0 6px'}}>
          Customer Tracking Communication
        </h4>

        <p style={{
          margin:'0 0 12px',
          color:'#777',
          fontSize:12
        }}>
          Send the tracking link only to the
          approved buyer or dropship recipient.
        </p>

        {!preview ? (
          <div style={{
            color:'#777',
            fontSize:12
          }}>
            Tracking information is not
            available yet.
          </div>
        ) : recipients.length === 0 ? (
          <div style={{
            color:'#8F2E24',
            fontSize:12
          }}>
            No eligible tracking recipient is
            available. Confirm the customer
            details first.
          </div>
        ) : (
          recipients.map(recipient => {
            const recipientType =
              recipient.recipient_type ||
              recipient.type ||
              'buyer';

            const message =
              recipient.message ||
              preview.message ||
              '';

            return (
              <div
                key={recipientType}
                style={{
                  marginTop:12,
                  paddingTop:12,
                  borderTop:
                    '1px solid #E8E0D4'
                }}
              >
                <strong>
                  {recipient.name ||
                    recipientType}
                </strong>

                <div style={{
                  marginTop:3,
                  color:'#777',
                  fontSize:11
                }}>
                  {recipient.phone || ''}
                  {recipient.phone &&
                    recipient.email
                    ? ' · '
                    : ''}
                  {recipient.email || ''}
                </div>

                <textarea
                  readOnly
                  rows={5}
                  style={{
                    ...field,
                    marginTop:8,
                    background:'#F8F5EF'
                  }}
                  value={message}
                />

                <div style={{
                  display:'flex',
                  flexWrap:'wrap',
                  gap:8,
                  marginTop:8
                }}>
                  <button
                    type="button"
                    style={{
                      ...button,
                      background:'#A8893D'
                    }}
                    onClick={() =>
                      communicate(
                        'copy',
                        recipient
                      )
                    }
                  >
                    Copy Message
                  </button>

                  {recipient.phone && (
                    <button
                      type="button"
                      style={button}
                      onClick={() =>
                        communicate(
                          'whatsapp',
                          recipient
                        )
                      }
                    >
                      Open WhatsApp
                    </button>
                  )}

                  {recipient.email && (
                    <button
                      type="button"
                      style={button}
                      onClick={() =>
                        communicate(
                          'email',
                          recipient
                        )
                      }
                    >
                      Send Email
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}

        {history.length > 0 && (
          <div style={{marginTop:16}}>
            <span style={label}>
              Communication History
            </span>

            {history.map(
              (entry, index) => (
                <div
                  key={entry.id || index}
                  style={{
                    padding:'8px 0',
                    borderTop:
                      '1px solid #EEE7DB',
                    fontSize:11
                  }}
                >
                  <strong>
                    {(entry.channel || '')
                      .replace(/_/g, ' ')}
                  </strong>
                  {' · '}
                  {(entry.recipient_type || '')
                    .replace(/_/g, ' ')}
                  {' · '}
                  {(entry.status || '')
                    .replace(/_/g, ' ')}
                  {entry.created_at
                    ? ' · ' +
                      new Date(
                        entry.created_at
                      ).toLocaleString(
                        'en-NG'
                      )
                    : ''}
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
